// ./src/hooks/useTallies.ts
//
// Data engine for event Tallies (named tap counters). Mirrors useNotes for CRUD,
// and adds a `tick` that is built for the tap-to-count board on mobile / flaky
// networks:
//   • the new value is applied to local state INSTANTLY (optimistic) so a tap
//     feels immediate regardless of latency;
//   • the server tallyTick fires in parallel with a per-tap idempotency key, so a
//     retry never double-counts;
//   • the authoritative server value reconciles only once a burst of taps has
//     fully settled (pending hits 0), so rapid taps never flicker or regress from
//     out-of-order responses.
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import { Tally, TallyService, TallyFilter, TallyInput, TallyTickAction, TALLY_TICK_ACTION, Pagination, SortItem, listPage } from "@/utils/api";
import { upsertBy, upsertManyBy, removeBy, removeManyBy, newUuid } from "@/utils";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

// ============================================
// Types
// ============================================

export interface UseTalliesOptions {
  pageSize?: number;
  initialFilters?: Partial<TallyFilter>;
  initialSort?: SortItem[];
  autoLoad?: boolean;
}

export interface UseTalliesReturn {
  tally: Tally | null;
  tallies: Tally[];
  loading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  isCreating: boolean;
  isUpdating: boolean;
  removingIds: Set<string>;
  isRemoving: (id?: string) => boolean;
  syncingIds: Set<string>;
  isSyncing: (id?: string) => boolean;

  filters: Partial<TallyFilter>;
  sortBy: SortItem[];

  get: (id: string) => Promise<OpResult>;
  list: (filter?: Partial<TallyFilter>, pagination?: Pagination, sortBy?: SortItem[], shouldReset?: boolean) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: TallyInput) => Promise<OpResult>;
  update: (id: string, params: TallyInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;
  tick: (id: string, action: TallyTickAction) => Promise<OpResult>;

  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  reset: () => void;
  getById: (id: string | undefined) => Tally | undefined;

  totalCount: number;

  lastError: OpResult | null;
  clearError: () => void;
}

// ============================================
// Defaults
// ============================================

// Stable board order — oldest first — matches the server's default sort so a tap
// (which bumps updated_at) never reshuffles the board.
const DEFAULT_SORT: SortItem[] = [{ name: "createdAt", order: "ASC" }];

const DEFAULT_FILTERS: Partial<TallyFilter> = {};

// ⚠ THE IDEMPOTENCY KEY IS STORED IN A `uuid` COLUMN (`tally_entry.client_ref`), so it must be a
// real UUID — not "close enough". The old local fallback here was
// `${Date.now()}-${Math.random()...}`, which Postgres refuses outright: the whole tick rolled back,
// the counter did not move, and the operator saw a generic failure. It only bites where
// `crypto.randomUUID` is missing — i.e. any NON-SECURE context, such as crew on a phone pointed at
// `http://192.168.x.x`, which is precisely where nobody develops. Use the shared helper.
const genClientRef = (): string => newUuid();

// Wait this long after the LAST tap before flushing a batch to the server. Rapid
// taps within the window collapse into ONE tallyTick(amount) call — so slow
// networks never lose counts and never fire a request per tap.
const FLUSH_DEBOUNCE_MS = 300;

// After a failed flush, requeue and retry the net delta after this delay.
const RETRY_MS = 2000;

// ============================================
// Hook
// ============================================

export const useTallies = (options: UseTalliesOptions = {}): UseTalliesReturn => {
  const { pageSize = DEFAULT_PAGE_SIZE, initialFilters = DEFAULT_FILTERS, initialSort = DEFAULT_SORT, autoLoad = true } = options;

  const { user } = useApp();
  const userId = user?.id ?? null;

  const pageSizeRef = useRef(pageSize);
  // The page size of the LAST read: a refresh re-runs that read, so a WHOLE_SET read stays whole (#118).
  const lastPageSizeRef = useRef<number>(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  const lastQueriedFilterRef = useRef<Partial<TallyFilter>>(initialFilters);
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  const listCallIdRef = useRef(0);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Tap batching state (per tally id):
  //  • pendingStepsRef  — net UNSENT taps (signed: +1 increment, -1 decrement).
  //  • inFlightRef      — a flush is currently awaiting the server.
  //  • flushTimersRef   — the debounce timer scheduled after the last tap.
  // We reconcile local state to the authoritative server value only once a tally
  // has NO pending taps and no flush in flight — so rapid taps never regress from
  // an out-of-order / stale response.
  const pendingStepsRef = useRef<Map<string, number>>(new Map());
  const inFlightRef = useRef<Set<string>>(new Set());
  const flushTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const flushRef = useRef<(id: string) => void>(() => {});

  const [tally, setTally] = useState<Tally | null>(null);
  const [tallies, setTallies] = useState<Tally[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state before the
   * skeleton and then the data. Reported across the whole event space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Server total for the current filter; hasMore is derived from it (never a page-full heuristic).
  const [total, setTotal] = useState(0);
  const hasMore = tallies.length < total;

  const [filters, setFilters] = useState<Partial<TallyFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  const [currentPage, setCurrentPage] = useState(1);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());

  const [lastError, setLastError] = useState<OpResult | null>(null);

  const isEmpty = useMemo(() => isLoaded && tallies.length === 0, [isLoaded, tallies.length]);

  // ── Removal / sync tracking ──
  const startRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));

      return next;
    });
  }, []);

  const stopRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));

      return next;
    });
  }, []);

  const isRemoving = useCallback((id?: string): boolean => (id ? removingIds.has(id) : removingIds.size > 0), [removingIds]);
  const isSyncing = useCallback((id?: string): boolean => (id ? syncingIds.has(id) : syncingIds.size > 0), [syncingIds]);

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    listCallIdRef.current++;
    flushTimersRef.current.forEach((timer) => clearTimeout(timer));
    flushTimersRef.current.clear();
    pendingStepsRef.current.clear();
    inFlightRef.current.clear();

    setTally(null);
    setTallies([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setTotal(0);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingIds(new Set());
    setSyncingIds(new Set());
  }, []);

  // ── Core reads ──
  const get = useCallback(async (id: string): Promise<OpResult> => {
    setLoading(true);
    setLastError(null);

    try {
      const result = await TallyService.get(id);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = (result.data as Tally[])[0];
        setTally(data);
        setTallies((prev) => upsertBy(prev, data));
        setLastError(null);

        return result;
      }

      setLastError(result);
      setTally(null);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);
      setTally(null);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  const list = useCallback(
    async (
      filterOverride: Partial<TallyFilter> = {},
      pagination?: Pagination,
      sortByOverride?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      lastPageSizeRef.current = targetPageSize;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
        setCurrentPage(1);
      }

      setLastError(null);

      setFilters(filterOverride);
      lastQueriedFilterRef.current = filterOverride;

      if (sortByOverride) {
        setSortBy(sortByOverride);
        lastQueriedSortRef.current = sortByOverride;
      }

      const currentSortBy = sortByOverride || lastQueriedSortRef.current;

      try {
        const result = await listPage(TallyService.list, filterOverride as TallyFilter, { page: targetPage, pageSize: targetPageSize }, currentSortBy);

        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data) ? (result.data as Tally[]) : [];

          if (isAppending) {
            setTallies((prev) => upsertManyBy(prev, newItems, "id", "end"));
          } else {
            setTallies(newItems);
          }

          if (typeof result.total === "number") {
            setTotal(result.total);
          } else if (!isAppending) {
            setTotal(newItems.length);
          }

          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLastError(null);

          return result;
        }

        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setTallies([]);
        }

        return result;
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setTallies([]);
        }

        return result;
      } finally {
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  const getMany = useCallback(async (ids: string[]): Promise<OpResult> => {
    if (ids.length === 0) {
      return new OpResult();
    }

    setLoading(true);
    setLastError(null);

    try {
      const result = await TallyService.getMany(ids);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const loadedItems = Array.isArray(result.data) ? (result.data as Tally[]) : [];
        setTallies((prev) => upsertManyBy(prev, loadedItems));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Mutations ──
  const create = useCallback(async (params: TallyInput): Promise<OpResult> => {
    setIsCreating(true);
    setLastError(null);

    try {
      const result = await TallyService.create(params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const newItem = (result.data as Tally[])[0];
        setTally(newItem);
        setTallies((prev) => upsertBy(prev, newItem));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsCreating(false);
    }
  }, []);

  const update = useCallback(async (id: string, params: TallyInput): Promise<OpResult> => {
    if (!id) {
      const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Tally ID is required for updating");
      setLastError(result);

      return result;
    }

    setIsUpdating(true);
    setLastError(null);

    try {
      const result = await TallyService.update({ id }, params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const updatedItem = (result.data as Tally[])[0];
        setTally(updatedItem);
        setTallies((prev) => upsertBy(prev, updatedItem));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Tally ID is required for removal");
        setLastError(result);

        return result;
      }

      startRemoving([id]);
      setLastError(null);

      try {
        const result = await TallyService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setTally(null);
          setTallies((prev) => removeBy(prev, id));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving([id]);
      }
    },
    [startRemoving, stopRemoving]
  );

  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "At least one tally must be selected");
        setLastError(result);

        return result;
      }

      startRemoving(ids);
      setLastError(null);

      try {
        const result = await TallyService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setTally(null);
          setTallies((prev) => removeManyBy(prev, ids));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving(ids);
      }
    },
    [startRemoving, stopRemoving]
  );

  // ── Tick (optimistic, batched tap-to-count) ──

  // id is "syncing" while it has unsent taps or an in-flight flush.
  const refreshSyncing = useCallback((id: string) => {
    const active = inFlightRef.current.has(id) || (pendingStepsRef.current.get(id) ?? 0) !== 0;

    setSyncingIds((prev) => {
      const has = prev.has(id);
      if (active === has) {
        return prev;
      }

      const next = new Set(prev);
      if (active) {
        next.add(id);
      } else {
        next.delete(id);
      }

      return next;
    });
  }, []);

  const scheduleFlush = useCallback((id: string, delay: number) => {
    const existing = flushTimersRef.current.get(id);
    if (existing) {
      clearTimeout(existing);
    }

    const timer = setTimeout(() => {
      flushTimersRef.current.delete(id);
      flushRef.current(id);
    }, delay);

    flushTimersRef.current.set(id, timer);
  }, []);

  // Send the net accumulated taps for one tally as a SINGLE tallyTick(amount).
  const flushTally = useCallback(
    async (id: string) => {
      const timer = flushTimersRef.current.get(id);
      if (timer) {
        clearTimeout(timer);
        flushTimersRef.current.delete(id);
      }

      // One flush at a time per tally; completion re-flushes any taps that piled up.
      if (inFlightRef.current.has(id)) {
        return;
      }

      const net = pendingStepsRef.current.get(id) ?? 0;
      if (net === 0) {
        refreshSyncing(id);

        return;
      }

      // Take the batch; new taps during the flight re-accumulate from 0.
      pendingStepsRef.current.set(id, 0);
      inFlightRef.current.add(id);
      refreshSyncing(id);

      const action = net > 0 ? TALLY_TICK_ACTION.INCREMENT : TALLY_TICK_ACTION.DECREMENT;
      const amount = Math.abs(net);
      const clientRef = genClientRef();

      try {
        const result = await TallyService.tick(id, action, amount, clientRef);
        inFlightRef.current.delete(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const remaining = pendingStepsRef.current.get(id) ?? 0;

          if (remaining === 0) {
            // Settled: snap to the authoritative server value.
            const server = (result.data as Tally[])[0];
            if (server?.id) {
              setTallies((prev) => prev.map((t) => (t.id === id ? { ...t, ...server } : t)));
            }

            refreshSyncing(id);
          } else {
            // More taps arrived mid-flight — send them (no reconcile yet).
            refreshSyncing(id);
            flushRef.current(id);
          }

          return;
        }

        // Failed: requeue the net (local stays optimistic) and retry later.
        pendingStepsRef.current.set(id, (pendingStepsRef.current.get(id) ?? 0) + net);
        setLastError(result);
        refreshSyncing(id);
        scheduleFlush(id, RETRY_MS);
      } catch (error) {
        inFlightRef.current.delete(id);
        pendingStepsRef.current.set(id, (pendingStepsRef.current.get(id) ?? 0) + net);
        setLastError(OpResult.fromException(error));
        refreshSyncing(id);
        scheduleFlush(id, RETRY_MS);
      }
    },
    [refreshSyncing, scheduleFlush]
  );

  flushRef.current = flushTally;

  const tick = useCallback(
    async (id: string, action: TallyTickAction): Promise<OpResult> => {
      if (!id) {
        return OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Tally ID is required");
      }

      // RESET is deliberate (confirmed) — clear the pending batch, snap the local
      // value to the start, and send it immediately (not batched).
      if (action === TALLY_TICK_ACTION.RESET) {
        pendingStepsRef.current.set(id, 0);
        const timer = flushTimersRef.current.get(id);
        if (timer) {
          clearTimeout(timer);
          flushTimersRef.current.delete(id);
        }

        setTallies((prev) => prev.map((t) => (t.id === id ? { ...t, currentValue: t.startValue ?? 1 } : t)));
        inFlightRef.current.add(id);
        refreshSyncing(id);

        try {
          const result = await TallyService.tick(id, TALLY_TICK_ACTION.RESET, 1, genClientRef());
          inFlightRef.current.delete(id);

          if (result.code === OP_RESULT_CODES.OK && result.data && (pendingStepsRef.current.get(id) ?? 0) === 0) {
            const server = (result.data as Tally[])[0];
            if (server?.id) {
              setTallies((prev) => prev.map((t) => (t.id === id ? { ...t, ...server } : t)));
            }
          } else if (result.code !== OP_RESULT_CODES.OK) {
            setLastError(result);
          }

          refreshSyncing(id);

          if ((pendingStepsRef.current.get(id) ?? 0) !== 0) {
            flushRef.current(id);
          }

          return result;
        } catch (error) {
          inFlightRef.current.delete(id);
          const result = OpResult.fromException(error);
          setLastError(result);
          refreshSyncing(id);

          return result;
        }
      }

      // Increment / decrement: apply the step locally NOW (instant), queue one net
      // tap, and (re)start the debounce so a burst collapses into a single call.
      const sign = action === TALLY_TICK_ACTION.INCREMENT ? 1 : -1;

      setTallies((prev) => prev.map((t) => (t.id === id ? { ...t, currentValue: (t.currentValue ?? 0) + sign * (t.stepBy ?? 1) } : t)));

      pendingStepsRef.current.set(id, (pendingStepsRef.current.get(id) ?? 0) + sign);
      refreshSyncing(id);

      if (!inFlightRef.current.has(id)) {
        scheduleFlush(id, FLUSH_DEBOUNCE_MS);
      }

      return new OpResult({ code: OP_RESULT_CODES.OK });
    },
    [refreshSyncing, scheduleFlush]
  );

  // Flush any pending taps + clear timers on unmount.
  useEffect(() => {
    const timers = flushTimersRef.current;

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  // ── Convenience ──
  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    const nextPage = currentPage + 1;

    await list(lastQueriedFilterRef.current, { page: nextPage, pageSize: pageSizeRef.current }, lastQueriedSortRef.current, false);
  }, [list, loading, loadingMore, hasMore, currentPage]);

  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(lastQueriedFilterRef.current, { page: 1, pageSize: lastPageSizeRef.current }, lastQueriedSortRef.current, true);
  }, [list]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(refresh, isLoaded);

  const getById = useCallback(
    (id: string | undefined): Tally | undefined => {
      if (!id) {
        return undefined;
      }

      return tallies.find((item) => item.id === id);
    },
    [tallies]
  );

  const totalCount = total;

  // ── Effects ──
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list(initialFiltersRef.current, { page: 1, pageSize: pageSizeRef.current }, initialSortRef.current, true);
      }

      return;
    }

    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list(initialFiltersRef.current, { page: 1, pageSize: pageSizeRef.current }, initialSortRef.current, true);
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ── Return ──
  return useMemo(
    () => ({
      tally,
      tallies,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      pageSize: pageSizeRef.current,
      isCreating,
      isUpdating,
      removingIds,
      isRemoving,
      syncingIds,
      isSyncing,
      filters,
      sortBy,
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,
      tick,
      loadMore,
      refresh,
      reset,
      getById,
      totalCount,
      lastError,
      clearError,
    }),
    [
      tally,
      tallies,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      isCreating,
      isUpdating,
      removingIds,
      isRemoving,
      syncingIds,
      isSyncing,
      filters,
      sortBy,
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,
      tick,
      loadMore,
      refresh,
      reset,
      getById,
      totalCount,
      lastError,
      clearError,
    ]
  );
};
